import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, UserCircle, LogOut, User, Search, GraduationCap, Rocket } from 'lucide-react';
import LiveDateTime from './LiveDateTime';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getDashboardLink = () => {
    if (user.role === 'super_admin') return '/super-admin';
    if (user.role === 'admin') return '/admin';
    return '/dashboard';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?search=${searchQuery}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div className="text-sm font-medium font-hindi hidden lg:block">
              🇮🇳 भारत का पहला Digital Research & Learning Platform
            </div>
            <div className="text-sm font-medium font-hindi lg:hidden">
              🇮🇳 MKDRP - Digital Research Platform
            </div>
            
            <div className="flex items-center gap-4">
              <LiveDateTime />
              
              {/* Social Media Icons */}
              <div className="hidden sm:flex items-center gap-2">
                <a 
                  href="https://www.youtube.com/channel/UCL1KvM2ltxqxMkWfOCO-n2Q"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 p-1.5 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="YouTube"
                  data-testid="youtube-link"
                  title="🔴 YouTube"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.instagram.com/ermanishkasyap/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 p-1.5 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="Instagram"
                  data-testid="instagram-link"
                  title="📷 Instagram"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.facebook.com/ErManishKasyapSOB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 p-1.5 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="Facebook"
                  data-testid="facebook-link"
                  title="👍 Facebook"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/902v0dmw_logo.png" alt="MKDRP Logo" className="h-12 w-12" />
            <div>
              <h1 className="text-xl font-bold font-heading gradient-text">MKDRP</h1>
              <p className="text-xs text-gray-600">Digital Research Platform</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-about">About Us</Link>
            <Link to="/courses" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-courses">Courses</Link>
            <Link to="/training" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-training">Training Program</Link>
            <Link to="/blog" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-blog">Blog</Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-600 font-medium transition-colors" data-testid="nav-contact">Contact Us</Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="search-button"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-gray-700" />
            </button>

            {token ? (
              <>
                <Link to={getDashboardLink()} className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium" data-testid="nav-dashboard">
                  <UserCircle className="w-5 h-5" />
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="flex items-center gap-2 text-gray-600 hover:text-red-600 font-medium" data-testid="logout-button">
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold transition-all"
                  data-testid="student-login-button"
                >
                  <GraduationCap className="w-4 h-4" />
                  Student Login
                </Link>
                <Link 
                  to="/checkout" 
                  className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-5 py-2 rounded-full font-bold hover:shadow-lg transition-all" 
                  data-testid="join-course-button"
                >
                  <Rocket className="w-4 h-4" />
                  Join Course
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden text-gray-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-4 border-t border-gray-100 animate-slideDown">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Search Courses / Research / Blog"
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-full focus:border-orange-500 focus:outline-none transition-colors"
                data-testid="search-input"
                autoFocus
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-full font-bold hover:shadow-lg transition-all"
              >
                Search
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden pb-4 space-y-3" data-testid="mobile-menu">
            <Link to="/" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Home</Link>
            <Link to="/about" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>About Us</Link>
            <Link to="/courses" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Courses</Link>
            <Link to="/training" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Training Program</Link>
            <Link to="/blog" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Blog</Link>
            <Link to="/contact" className="block text-gray-700 hover:text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
            {token ? (
              <>
                <Link to={getDashboardLink()} className="block text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
                <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="block text-red-600 font-medium w-full text-left">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-orange-600 font-medium" onClick={() => setIsMenuOpen(false)}>🎓 Student Login</Link>
                <Link to="/checkout" className="block bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full font-bold text-center" onClick={() => setIsMenuOpen(false)}>🚀 Join Course</Link>
              </>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
